import React, { useCallback, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import messages from '../../messages';
import { AutomationRulesContext } from '../../context';
import { FilterEditorContext } from '../../../../../components/filter-editor/context';
import { useOpenRuleDrawer } from '../../hooks/use-open-rule-drawer';
import { EventSourceStrings } from '../../../../../tracking/tracking-constants';
import { AutomationRulesSteps, useTrackAutomationRulesWorkflow } from '../../hooks/use-track-automation-rules-workflow';

const getFilterIdForCopying = (ruleFilterId, newFilterId, filterSets) => {
  // if the automation rule is using an ad-hoc filter (not an existing order filter)  // then we need to avoid using the existing filterId or it causes a bug
  // but if it is using an existing order filter we need to keep the id
  // same for getFilterSetForCopying
  if (!ruleFilterId) {
    return ruleFilterId;
  }
  const filterSet = filterSets[ruleFilterId];
  if (filterSet?.active) {
    return ruleFilterId;
  }
  return newFilterId;
};

const getFilterSetForCopying = (filterIdToCopy, newFilterId, filterSets) => {
  if (!filterIdToCopy || !filterSets[filterIdToCopy]) {
    return { appliedFilters: {} };
  }

  const filterSetToCopy = filterSets[filterIdToCopy];
  if (filterSetToCopy.active) {
    return filterSetToCopy;
  }
  return {
    orderFilterSetId: newFilterId,
    appliedFilters: filterSetToCopy.appliedFilters,
  };
};

/** @param {{ automationRuleId: string }} props */
export const RuleCopyLink = ({ automationRuleId }) => {
  const { automationRules } = useContext(AutomationRulesContext);
  const { filterSets, getNewTemporaryFilter } = useContext(FilterEditorContext);
  const openRuleDrawer = useOpenRuleDrawer();
  const trackStart = useTrackAutomationRulesWorkflow(AutomationRulesSteps.START);
  const intl = useIntl();
  const openDrawer = useCallback(() => {
    const newFilterId = getNewTemporaryFilter();
    const rule = automationRules.find((r) => r.automationRuleId === automationRuleId);
    const copiedRule = {
      ...rule,
      filterId: getFilterIdForCopying(rule.filterId, newFilterId, filterSets),
    };

    // delete the automationRuleId so its treated as a new rule
    delete copiedRule.automationRuleId;

    openRuleDrawer({
      rule: copiedRule,
      name: intl.formatMessage(messages.copiedRuleName, { name: rule.name }),
      filterSet: getFilterSetForCopying(rule.filterId, newFilterId, filterSets),
    });
    trackStart({
      rule,
      source: EventSourceStrings.RULE_COPY_LINK,
      isAiGenerated: false,
    });
  }, [automationRuleId, automationRules, openRuleDrawer, filterSets, intl, getNewTemporaryFilter, trackStart]);

  return (
    <DropdownMenuItem onClick={openDrawer} theme='link'>
      <FormattedMessage {...messages.copyButton} />
    </DropdownMenuItem>
  );
};
